import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  InputAdornment,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  useMediaQuery,
  Avatar,
  Card,
  Menu,
  MenuItem,
} from "@mui/material";
import { Search, MoreVert, FilterListOutlined } from "@mui/icons-material";

import { imageSource, Strings } from "@app/constants";
import { FormProvider, RHFTextField } from "@app/components";
import { useDispatch, useSelector } from "react-redux";
import { useRequestState } from "@app/hooks";
import { useTheme } from "@mui/material/styles";
import { selectCustomerState } from "@app/modules/salesperson/selector";
import { customer, deleteSalesPerson } from "@app/modules/salesperson/slice";

const CustomerPage = () => {
  const methods = useForm();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const itemsPerPage = 8;
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [selectedRole, setSelectedRole] = useState<string>("");

  const openFilter = Boolean(filterAnchor);
  const [editBrandDialog, setEditBrandDialog] = useState(false);

  const { data: customerData } = useRequestState({
    stateSelector: selectCustomerState,
    errorShown: true,
  });

  const customerDatshow = customerData || [];
  const dispatch = useDispatch();

  const handleDelete = (id: any) => {
    if (id) {
      dispatch(deleteSalesPerson({ salesPersonId: id }));
    }
  };

  useEffect(() => {
    dispatch(customer(selectedRole ? selectedRole : "Customer"));
  }, [selectedRole]);

  const filteredData = customerDatshow.filter((item: any) => {
    const query = searchQuery.toLowerCase();
    const fullName =
      `${item.firstName ?? ""} ${item.lastName ?? ""}`.toLowerCase();

    const matchesSearch =
      fullName.includes(query) ||
      item.phone?.toLowerCase().includes(query) ||
      item.email?.toLowerCase().includes(query);

    const matchesRole = selectedRole
      ? item.role?.toLowerCase() === selectedRole.toLowerCase()
      : true;

    return matchesSearch && matchesRole;
  });

  const displayedProducts = filteredData.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  const fontFamily = Strings.fontLanguage.workSans;

  return (
    <FormProvider methods={methods}>
      <Stack direction="row" sx={{ minHeight: "100vh" }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Card
            sx={{
              p: 2,
              flex: 1,
              overflowY: "auto",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              flexWrap="wrap"
              rowGap={2}
              mb={3}
            >
              <RHFTextField
                name="search"
                placeholder="Search"
                size="small"
                value={searchQuery}
                onChange={(e: any) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={(e) => setFilterAnchor(e.currentTarget)}
                      >
                        <Badge
                          color="primary"
                          variant={selectedRole ? "dot" : "standard"}
                        >
                          <FilterListOutlined />{" "}
                        </Badge>
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Menu
                anchorEl={filterAnchor}
                open={openFilter}
                onClose={() => setFilterAnchor(null)}
              >
                {["Buyer", "Seller", "Investor"].map((role) => (
                  <MenuItem
                    key={role}
                    selected={selectedRole === role}
                    onClick={() => {
                      setSelectedRole(role);
                      setFilterAnchor(null);
                      setPage(1);

                      dispatch(customer({ role }));
                    }}
                  >
                    {role}
                  </MenuItem>
                ))}

                <MenuItem
                  onClick={() => {
                    setSelectedRole("");
                    setFilterAnchor(null);
                    dispatch(customer("Customer"));
                  }}
                >
                  Clear Filter
                </MenuItem>
              </Menu>
            </Stack>
            <TableContainer component={Paper} sx={{ pb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      "Customer",
                      "Contact No.",
                      "Email",
                      "Role",
                      "Assigned To",
                    ].map((heading) => (
                      <TableCell
                        key={heading}
                        align={heading == "Customer" ? "left" : "center"}
                        sx={{
                          fontWeight: 600,
                          fontFamily: fontFamily,
                          color: "#474747",
                        }}
                      >
                        {heading}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedProducts.map((item: any, index: any) => (
                    <TableRow key={index}>
                      <TableCell
                        sx={{ color: "#5F5F5F", fontWeight: 600 }}
                        align="center"
                      >
                        <Stack
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              right: 12,
                              fontSize: 14,
                            }}
                            src={
                              item.profileImage ||
                              "https://randomuser.me/api/portraits/men/75.jpg"
                            }
                          >
                            {item.name?.charAt(0)?.toUpperCase() || "?"}
                          </Avatar>
                          <Box
                            component="span"
                            sx={{ whiteSpace: "nowrap", alignItems: "center" }}
                          >
                            {item.firstName + " " + item.lastName || "N/A"}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell
                        sx={{ color: "#5F5F5F", fontWeight: 600 }}
                        align="center"
                      >
                        {item.phone || "N/A"}
                      </TableCell>
                      <TableCell
                        sx={{ color: "#5F5F5F", fontWeight: 600 }}
                        align="center"
                      >
                        {item.email || "N/A"}
                      </TableCell>
                      <TableCell
                        sx={{ color: "#5F5F5F", fontWeight: 600 }}
                        align="center"
                      >
                        {item.role || "N/A"}
                      </TableCell>
                      <TableCell
                        sx={{ color: "#5F5F5F", fontWeight: 600 }}
                        align="center"
                      >
                        {item?.firstName
                          ? `${item?.firstName ?? ""} ${item?.lastName ?? ""}`
                          : "N/A"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Stack
                direction={isSmallScreen ? "column" : "row"}
                justifyContent="flex-end"
                alignItems="center"
                spacing={2}
                mt={2}
              >
                <Typography
                  sx={{ fontSize: 14, fontFamily: fontFamily, fontWeight: 400 }}
                >
                  {`Showing ${(page - 1) * itemsPerPage + 1} to ${Math.min(
                    page * itemsPerPage,
                    filteredData.length,
                  )} of ${filteredData.length} entries`}
                </Typography>
                <Pagination
                  count={Math.ceil(filteredData.length / itemsPerPage)}
                  page={page}
                  onChange={(event: any, value: any) => setPage(value)}
                  shape="rounded"
                />
              </Stack>
            </TableContainer>
          </Card>
        </Box>
      </Stack>
    </FormProvider>
  );
};

export { CustomerPage };
